// Общее для кабинета администратора: данные /api/admin/* (pravosvod/admin.py), даты, числа, карточка с цифрой.
import type { ReactNode } from "react";
import { Icon } from "../components/Icon";
import { plural } from "../lib/text";

export type Stats = {
  registered: number; guests: number; registered_week: number; guests_week: number; active_day: number; registered_day: number;
  new_day: number; registered_new_day: number; assistant_today: number; documents_today: number;
  chats: number; docs: number; calcs: number; uploads: number;
};
export type Filter = "all" | "registered" | "guests" | "day" | "week" | "active";
export type Sort = "name" | "created" | "seen";
export type Provider = "" | "google" | "yandex" | "email" | "none";
export type AdminUser = {
  id: number; kind: "guest" | "registered"; name: string; email: string; created_at: string; last_seen_at: string;
  last_ip: string; created_ip: string; browser: string; providers: string[];
  chats: number; docs: number; calcs: number; uploads: number; folders: number; assistant_total: number; assistant_today: number;
  last_chat: string | null; last_doc: string | null; last_calc: string | null; last_upload: string | null; last_assistant: string | null;
};
export type UserList = { items: AdminUser[]; total: number; counts: Record<Filter, number> };
export type Detail = {
  logins: { provider: string; email: string; created_at: string }[]; sessions: number; user_agent: string;
  recent: { kind: string; title: string; at: string }[]; usage: { day: string; assistant: number; documents: number }[];
};
export type Day = { day: string; registered: number; guests: number; assistant: number; documents: number };
export type LawCode = { key: string; name: string; kind?: string; articles: number; checked: string; checked_on: string; chunks: number; vectors: number };
export type AdminEvent = { kind: string; title: string; at: string; user_id: number; user_kind: string; name: string; email: string };

export const KINDS: Record<string, string> = {
  chat: "Чат", doc: "Документ", calc: "Расчёт", upload: "Загрузка", login: "Вход",
};
export const PROVIDER_NAMES: Record<string, string> = { google: "Google", yandex: "Яндекс ID", email: "Почта" };

export const num = (n: number) => n.toLocaleString("ru-RU");

export const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString("ru-RU", {
  day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
}) : "—");

export const shortDay = (iso: string) => new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "short" });

export function ago(iso: string | null): string {
  if (!iso) return "—";
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 2) return "только что";
  if (minutes < 60) return `${plural(minutes, "минуту", "минуты", "минут")} назад`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${plural(hours, "час", "часа", "часов")} назад`;
  const days = Math.round(hours / 24);
  return `${plural(days, "день", "дня", "дней")} назад`;
}

export const userTitle = (user: { name: string; email: string; id?: number; user_id?: number }) =>
  user.name || user.email || `Гость № ${user.id ?? user.user_id}`;

// Карточка с цифрой вверху раздела, как в «Непростоботе»: значок в цветном круге, подпись, число; у широкой — полоса.
export function StatCard({ icon, tone, label, value, hint, share }: {
  icon: string; tone: "red" | "gray" | "amber" | "green" | "blue"; label: ReactNode; value: ReactNode; hint?: ReactNode; share?: number;
}) {
  return (
    <li className={`adm-stat is-${tone}`}>
      <span className="adm-stat-icon"><Icon name={icon} /></span>
      <span className="adm-stat-body">
        <span className="adm-stat-label">{label}</span>
        <span className="adm-stat-value">{value}</span>
        {hint && <span className="adm-stat-hint">{hint}</span>}
        {share !== undefined && <span className="adm-progress"><span style={{ width: `${Math.round(Math.min(1, share) * 100)}%` }} /></span>}
      </span>
    </li>
  );
}
